import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { LEXOS_PILLARS, AGENT_FIRST, HUMAN_FIRST, STATUS_META } from "./navData";

const StatusChip = ({ status }) => {
    if (!status) return null;
    const meta = STATUS_META[status];
    if (!meta) return null;
    return (
        <span
            className="ml-2 inline-flex items-center gap-1 px-1.5 py-px rounded-full text-[8.5px] font-mono uppercase tracking-[0.12em] font-semibold flex-shrink-0"
            style={{ background: meta.bg, color: meta.color }}
        >
            <span
                className={`inline-block w-1 h-1 rounded-full ${status === "live" ? "animate-pulse" : ""}`}
                style={{ background: meta.dot }}
            />
            {status === "soon" ? "Soon" : status === "beta" ? "Early" : "Live"}
        </span>
    );
};

const Col = ({ label, items, accent, onItemClick }) => (
    <div className="flex flex-col gap-1">
        <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[var(--ink-dim)] mb-2 px-3">
            {label}
        </div>
        {items.map((it) => (
            <Link
                key={it.id}
                to="/platform"
                onClick={onItemClick}
                data-testid={`mega-${it.id}`}
                className={`group flex items-start gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    it.accent
                        ? "bg-[rgba(30,91,255,0.07)] hover:bg-[rgba(30,91,255,0.12)]"
                        : "hover:bg-[var(--cream-2)]"
                }`}
            >
                <span
                    className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                        accent || it.accent
                            ? "bg-[var(--blue)] text-white"
                            : "bg-[var(--cream-2)] text-[var(--ink)] group-hover:bg-[var(--paper)]"
                    }`}
                >
                    <i className={`fa-solid ${it.icon} text-[12px]`} />
                </span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <div className={`text-[13px] font-display font-semibold leading-tight ${
                            it.accent ? "accent-blue" : "text-[var(--ink)]"
                        }`}>
                            {it.name}
                        </div>
                        <StatusChip status={it.status} />
                    </div>
                    <div className="text-[11px] text-[var(--ink-soft)] leading-snug mt-0.5">
                        {it.tagline}
                    </div>
                </div>
            </Link>
        ))}
    </div>
);

const PlatformMegaMenu = ({ open, onClose }) => {
    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                    data-testid="platform-mega-menu"
                    className="mt-2 mx-auto w-full max-w-[960px] bg-[var(--paper)] border border-[var(--border-strong)] rounded-2xl shadow-[0_30px_60px_-20px_rgba(35,31,35,0.25)] p-6 z-[55]"
                >
                    <div className="grid grid-cols-3 gap-5">
                        <Col label="LexOPS Platform" items={LEXOS_PILLARS} accent onItemClick={onClose} />
                        <Col label="Agent-First — autonomous workers" items={AGENT_FIRST} onItemClick={onClose} />
                        <Col label="Human-First — co-pilots & tools" items={HUMAN_FIRST} onItemClick={onClose} />
                    </div>

                    <div className="mt-5 pt-4 border-t border-[var(--border)] flex items-center justify-between gap-4 flex-wrap">
                        <div className="text-[12px] text-[var(--ink-soft)]">
                            <span className="font-semibold text-[var(--ink)]">LexOPS</span> · the operating system for AI-native legal ops teams.
                        </div>
                        <Link
                            to="/platform"
                            onClick={onClose}
                            data-testid="mega-explore-platform"
                            className="inline-flex items-center gap-2 text-[12px] font-medium accent-blue hover:underline"
                        >
                            Explore the full platform
                            <i className="fa-solid fa-arrow-right text-[10px]" />
                        </Link>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default PlatformMegaMenu;
